import { TranslocoService } from "@jsverse/transloco";
import { afterRenderEffect, computed, inject, Injectable, Injector, RendererFactory2, signal } from "@angular/core";

import { environment } from "@azm/environments/environment";

enum Languages {
	EN = "en",
	AR = "ar",
}

@Injectable({
	providedIn: "root",
})
export class TranslateService {
	readonly #injector = inject(Injector);
	readonly #rendererFactory = inject(RendererFactory2);
	readonly #translocoService = inject(TranslocoService);
	readonly #renderer = this.#rendererFactory.createRenderer(null, null);

	currentLanguage = signal<Languages | null>(null);

	isEnglish = computed(() => this.currentLanguage() === Languages.EN);

	changeLanguage(language: keyof typeof Languages) {
		const lang = Languages[language];
		this.currentLanguage.set(lang);
		localStorage.setItem("azm-language", lang);
		this.#translocoService.load(lang).subscribe(() => {
			this.#translocoService.setActiveLang(lang);
			this.#handleBasicLogic();
		});
	}

	initializeLanguage() {
		afterRenderEffect(
			() => {
				if (!this.currentLanguage()) {
					const lang = this.#resolveInitialLanguage();
					this.currentLanguage.set(lang);
					localStorage.setItem("azm-language", lang);
					this.#translocoService.load(lang).subscribe(() => {
						this.#translocoService.setActiveLang(lang);
						this.#handleBasicLogic();
					});
				}
			},
			{ injector: this.#injector },
		);
	}

	#resolveInitialLanguage(): Languages {
		const stored = localStorage.getItem("azm-language");
		if (stored === "en" || stored === "ar") {
			return stored as Languages;
		}

		const raw = environment.i18n_Configurations?.["defaultLanguage"];
		if (raw === "en" || raw === "ar") {
			return raw as Languages;
		}

		return Languages.AR;
	}

	#handleBasicLogic() {
		if (this.currentLanguage() === Languages.AR) {
			this.#renderer.addClass(document.body, "rtl");
			this.#renderer.setAttribute(document.body, "dir", "rtl");
			this.#renderer.setAttribute(document.querySelector("html"), "lang", Languages.AR);
		} else {
			this.#renderer.removeClass(document.body, "rtl");
			this.#renderer.setAttribute(document.body, "dir", "ltr");
			this.#renderer.setAttribute(document.querySelector("html"), "lang", Languages.EN);
		}
	}
}
